import React, { useState } from 'react';
import { Card, Form, Input, Button, message, Descriptions } from 'antd';
import { useSelector } from 'react-redux';
import { LockOutlined } from '@ant-design/icons';
import { changePassword } from '../../services/auth';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import './AccountSettingsPage.css';

/**
 * 账户设置
 * 职责：展示当前用户基础信息并支持修改密码；后续扩展其他设置项。
 * 数据：从全局 `auth` 状态读取用户信息；密码修改调用服务层接口并提示结果。
 * 交互：表单校验与提交、成功后重置表单。
 */
const AccountSettingsPage = () => {
  const { isMobile } = useBreakpoint();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { user } = useSelector(state => state.auth);

  // 修改密码：调用服务后提示成功/失败，并在成功时重置表单
  const handleChangePassword = async (values) => {
    setLoading(true);
    try {
      await changePassword(values.oldPassword, values.newPassword);
      message.success('密码修改成功');
      form.resetFields();
    } catch (error) {
      message.error(error.message || '密码修改失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container p-profile-account-settings">
      <div className="page-content space-y-md">
        <div className="page-title">账户设置</div>

        <Card className="card-compact" title="账户信息">
          <Descriptions column={isMobile ? 1 : 2} labelStyle={{ width: 120 }}>
            <Descriptions.Item label="用户名">
              {user?.username || user?.loginName || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="手机号">
              {user?.phone || user?.mobile || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="邮箱">
              {user?.email || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="用户类型">
              {user?.userType === 'ENTERPRISE' ? '企业用户' : '个人用户'}
            </Descriptions.Item>
          </Descriptions>
        </Card>
        
        <Card className="card-compact" title="修改密码">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleChangePassword}
            style={{ maxWidth: isMobile ? '100%' : 420 }}
          >
          <Form.Item
            name="oldPassword"
            label="当前密码"
            rules={[
              { required: true, message: '请输入当前密码' },
              { min: 6, message: '密码至少6个字符' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="请输入当前密码"
            />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码至少6个字符' },
              { max: 20, message: '密码最多20个字符' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="请输入新密码"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="确认新密码"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="请再次输入新密码"
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block={isMobile}>
              修改密码
            </Button>
          </Form.Item>
        </Form>
      </Card>

      </div>
    </div>
  );
};

export default AccountSettingsPage;
